import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Trash2, Copy, Power, RefreshCw, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatErr, copyText, PUBLIC_BASE_URL } from "@/lib/api";

const genPassword = () => {
  const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const arr = new Uint32Array(12);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join("");
};

const credText = (email, password) => `Accesso SALVILO\n${PUBLIC_BASE_URL}/login\nEmail: ${email}\nPassword: ${password}\nAl primo accesso ti verrà chiesto di scegliere una tua password.`;
const onPublicHost = () => !PUBLIC_BASE_URL || PUBLIC_BASE_URL.includes(window.location.hostname);

const CredDialog = ({ cred, onClose }) => (
  <Dialog open={!!cred} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-md" data-testid="client-cred-dialog">
      <DialogHeader><DialogTitle>Credenziali da inviare alla cliente</DialogTitle></DialogHeader>
      {cred && <>
        <pre className="whitespace-pre-wrap rounded-xl bg-zinc-50 p-4 font-mono-ui text-xs leading-relaxed text-zinc-800" data-testid="client-cred-text">{credText(cred.email, cred.password)}</pre>
        <p className="text-xs text-zinc-500">Questa password non sarà più visibile dopo aver chiuso la finestra. Al primo accesso la cliente dovrà sceglierne una sua.</p>
        <Button onClick={async () => { await copyText(credText(cred.email, cred.password)); toast.success("Credenziali copiate"); }} data-testid="client-cred-copy"><Copy className="mr-1.5 h-4 w-4" />Copia credenziali</Button>
      </>}
    </DialogContent>
  </Dialog>
);

export const ClientAccessPanel = ({ client }) => {
  const [users, setUsers] = useState([]);
  const [cred, setCred] = useState(null);
  const [email, setEmail] = useState(client.email || "");
  const [password, setPassword] = useState(genPassword);
  const load = () => api.get(`/clients/${client.id}/users`).then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const create = () => run(async () => {
    await api.post(`/clients/${client.id}/users`, { email, password, name: client.name });
    setCred({ email: email.trim().toLowerCase(), password });
    setPassword(genPassword());
  }, "Accesso creato");
  const reset = (u) => {
    if (!window.confirm(`Generare una nuova password per ${u.email}? Quella attuale smetterà di funzionare.`)) return;
    const p = genPassword();
    run(async () => {
      await api.put(`/client-users/${u.id}`, { password: p });
      setCred({ email: u.email, password: p });
    }, "Nuova password generata");
  };

  return (
    <aside className="panel h-fit space-y-4 p-6" data-testid="client-access-panel">
      <div className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-terra" /><span className="eyebrow">Accesso cliente</span></div>
      <p className="text-xs text-zinc-500">La cliente potrà vedere le statistiche e modificare da sola contenuti, link, foto e PDF delle sue card. Al primo accesso sceglierà una sua password. Puoi sospendere l'accesso in qualsiasi momento.</p>
      {!onPublicHost() && (
        <div className="flex gap-2 rounded-lg bg-amber-50 p-3 text-[11px] leading-relaxed text-amber-800" data-testid="client-access-preview-warning"><AlertTriangle className="h-4 w-4 shrink-0" />Sei nell'anteprima: gli accessi creati qui non esistono su salvilo.it. Per le clienti vere crea gli accessi dal pannello su salvilo.it.</div>
      )}
      {users.map((u) => (
        <div key={u.id} className="rounded-lg border bg-white p-3 text-sm" data-testid={`client-user-${u.email}`}>
          <div className="flex items-center justify-between gap-2"><span className="truncate font-medium">{u.email}</span><span className={`text-[11px] ${u.active ? "text-emerald-600" : "text-red-500"}`} data-testid={`client-user-status-${u.email}`}>{u.active ? "Attivo" : "Disattivato"}</span></div>
          <div className="mt-0.5 text-[11px] text-zinc-400">{u.last_login ? `Ultimo accesso ${new Date(u.last_login).toLocaleDateString("it-IT")}` : "Mai effettuato l'accesso"} · <span data-testid={`client-user-pwd-${u.email}`}>{u.must_change_password ? "password provvisoria" : "password scelta dalla cliente"}</span></div>
          <div className="mt-2 flex gap-3 text-xs">
            <button className="flex items-center gap-1 text-terra" onClick={() => reset(u)} data-testid={`client-user-reset-${u.email}`}><RefreshCw className="h-3 w-3" />Nuova password</button>
            <button className="flex items-center gap-1 text-zinc-500 hover:text-black" onClick={() => run(() => api.put(`/client-users/${u.id}`, { active: !u.active }), u.active ? "Account disattivato" : "Account riattivato")} data-testid={`client-user-toggle-${u.email}`}><Power className="h-3 w-3" />{u.active ? "Disattiva" : "Riattiva"}</button>
            <button className="ml-auto text-zinc-400 hover:text-red-600" onClick={() => window.confirm(`Eliminare l'accesso ${u.email}?`) && run(() => api.delete(`/client-users/${u.id}`), "Accesso eliminato")} data-testid={`client-user-delete-${u.email}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      ))}
      <div className="space-y-2 border-t pt-4">
        <Input data-testid="client-access-email" placeholder="Email della cliente" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white" />
        <div className="flex gap-2">
          <Input data-testid="client-access-password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-white font-mono-ui text-xs" />
          <Button variant="outline" size="sm" className="h-9" onClick={() => setPassword(genPassword())} title="Genera nuova password" data-testid="client-access-generate"><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
        <Button className="w-full" onClick={create} disabled={!email || password.length < 8} data-testid="client-access-create">Crea accesso</Button>
      </div>
      <CredDialog cred={cred} onClose={() => setCred(null)} />
    </aside>
  );
};
